/* PXL PDP Product Specs
 * Custom Element <pxl-product-specs>
 * - Tab-Switching (Desktop)
 * - Accordion-Toggle (Mobile)
 * - Variant-reaktiv: hört auf variantChangeEnd vom <variant-options> und
 *   refetched die Section per ?section_id=...&variant=...
 * - Combined Listings: Nook tauscht beim Wechsel auf ein anderes Child-Produkt
 *   nur <variant-options>, Galerie und URL aus (handleCombinedListings in
 *   variants.js) und feuert dabei KEIN variantChangeEnd. Ein MutationObserver
 *   auf data-url des Haupt-<variant-options> erkennt den Swap und lädt die
 *   Section vom neuen Child-Produkt nach.
 * - Keyboard-Navigation auf Tabs (ArrowLeft/Right/Home/End)
 */
(function () {
  'use strict';

  if (customElements.get('pxl-product-specs')) {
    return;
  }

  class PxlProductSpecs extends HTMLElement {
    constructor() {
      super();
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeyDown = this.handleTabKeyDown.bind(this);
      this.handleAccordionClick = this.handleAccordionClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.requestToken = 0;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.productHandle = this.dataset.productHandle;
      this.bindInternal();
      document.addEventListener('variantChangeEnd', this.handleVariantChange);
      this.observeCombinedListingSwap();
    }

    disconnectedCallback() {
      document.removeEventListener('variantChangeEnd', this.handleVariantChange);
      if (this.swapObserver) {
        this.swapObserver.disconnect();
        this.swapObserver = null;
      }
    }

    /* ---------- Haupt-<variant-options> dieses Produkts finden ---------- */
    findMainVariantOptions() {
      const all = document.querySelectorAll('variant-options');
      for (let i = 0; i < all.length; i++) {
        const vo = all[i];
        if (
          (this.productUrl && vo.dataset.url === this.productUrl) ||
          (this.productHandle && vo.dataset.productHandle === this.productHandle)
        ) {
          return vo;
        }
      }
      return null;
    }

    /* ---------- Combined-Listing-Swap erkennen ---------- */
    observeCombinedListingSwap() {
      this.mainVariantOptions = this.findMainVariantOptions();
      if (!this.mainVariantOptions || typeof MutationObserver === 'undefined') return;

      const vo = this.mainVariantOptions;
      this.swapObserver = new MutationObserver(() => {
        const newUrl = vo.dataset.url;
        if (!newUrl || newUrl === this.productUrl) return;

        // Neues Child-Produkt übernehmen
        this.productUrl = newUrl;
        this.productHandle = vo.dataset.productHandle || this.productHandle;

        const variantId = vo.currentVariant && vo.currentVariant.id;
        this.refresh(newUrl, variantId);
      });
      this.swapObserver.observe(vo, { attributes: true, attributeFilter: ['data-url'] });
    }

    /* ---------- Internal element bindings ---------- */
    bindInternal() {
      this.tabs = this.querySelectorAll('.pxl-product-specs__tab');
      this.panels = this.querySelectorAll('.pxl-product-specs__panel');
      this.triggers = this.querySelectorAll('.pxl-product-specs__accordion-trigger');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleTabKeyDown);
      });
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleAccordionClick);
      });
    }

    /* ---------- Tab switching ---------- */
    handleTabClick(event) {
      const tab = event.currentTarget;
      this.activateTab(tab.dataset.tab);
    }

    activateTab(target) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tab === target;
        tab.classList.toggle('pxl-product-specs__tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          tab.removeAttribute('tabindex');
        } else {
          tab.setAttribute('tabindex', '-1');
        }
      });
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === target;
        panel.classList.toggle('pxl-product-specs__panel--active', isActive);
        // Sync Mobile-Open mit Active für konsistentes Verhalten beim Resize
        panel.classList.toggle('pxl-product-specs__panel--mobile-open', isActive);
        const trigger = panel.querySelector('.pxl-product-specs__accordion-trigger');
        if (trigger) {
          trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        }
      });
    }

    handleTabKeyDown(event) {
      const key = event.key;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
        return;
      }
      event.preventDefault();
      const tabsArr = Array.from(this.tabs);
      const currentIndex = tabsArr.indexOf(event.currentTarget);
      let nextIndex;
      if (key === 'ArrowLeft') {
        nextIndex = currentIndex === 0 ? tabsArr.length - 1 : currentIndex - 1;
      } else if (key === 'ArrowRight') {
        nextIndex = currentIndex === tabsArr.length - 1 ? 0 : currentIndex + 1;
      } else if (key === 'Home') {
        nextIndex = 0;
      } else {
        nextIndex = tabsArr.length - 1;
      }
      const nextTab = tabsArr[nextIndex];
      if (!nextTab) return;
      this.activateTab(nextTab.dataset.tab);
      nextTab.focus();
    }

    /* ---------- Accordion toggle (Mobile) ---------- */
    handleAccordionClick(event) {
      const trigger = event.currentTarget;
      const panel = trigger.closest('.pxl-product-specs__panel');
      if (!panel) return;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.classList.toggle('pxl-product-specs__panel--mobile-open', !isOpen);
    }

    /* ---------- Variant change (normaler Variantenwechsel) ---------- */
    handleVariantChange(event) {
      const variantOptions = event.target;
      if (!variantOptions || typeof variantOptions.tagName !== 'string') return;
      if (variantOptions.tagName.toLowerCase() !== 'variant-options') return;

      // Nur reagieren, wenn das Event von UNSEREM Produkt kommt.
      // Bevorzugt Identitätsvergleich mit dem Haupt-<variant-options>,
      // weil dessen data-url sich bei Combined Listings ändert.
      if (this.mainVariantOptions) {
        if (variantOptions !== this.mainVariantOptions) return;
      } else {
        const eventUrl = variantOptions.dataset && variantOptions.dataset.url;
        const eventHandle = variantOptions.dataset && variantOptions.dataset.productHandle;
        if (eventUrl && this.productUrl && eventUrl !== this.productUrl) return;
        if (eventHandle && this.productHandle && eventHandle !== this.productHandle) return;
      }

      const variant = variantOptions.currentVariant;
      if (!variant || !variant.id) return;

      const url = (variantOptions.dataset && variantOptions.dataset.url) || this.productUrl;
      this.refresh(url, variant.id);
    }

    /* ---------- Section neu laden ---------- */
    async refresh(url, variantId) {
      if (!url || !this.sectionId) return;

      // Nur die letzte Anfrage gewinnt (schnelle Klicks)
      const token = ++this.requestToken;

      let fetchUrl = url + '?section_id=' + encodeURIComponent(this.sectionId);
      if (variantId) {
        fetchUrl += '&variant=' + variantId;
      }

      try {
        const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
        if (!response.ok || token !== this.requestToken) return;

        const text = await response.text();
        if (token !== this.requestToken) return;

        const dom = new DOMParser().parseFromString(text, 'text/html');
        const newEl = dom.querySelector('pxl-product-specs');

        if (!newEl) {
          // Kein Inhalt für dieses Produkt/diese Variante → Section ausblenden
          this.classList.add('pxl-product-specs--empty');
          return;
        }

        // Aktuell aktiven Tab merken
        const activeTabEl = this.querySelector('.pxl-product-specs__tab--active');
        const currentTab = activeTabEl ? activeTabEl.dataset.tab : null;

        this.classList.remove('pxl-product-specs--empty');
        this.innerHTML = newEl.innerHTML;
        this.bindInternal();

        // Aktiven Tab wiederherstellen, falls noch vorhanden
        if (currentTab) {
          const restored = this.querySelector(
            '.pxl-product-specs__tab[data-tab="' + currentTab + '"]'
          );
          if (restored) {
            this.activateTab(currentTab);
          }
        }
      } catch (err) {
        console.error('[pxl-product-specs] section refresh failed', err);
      }
    }
  }

  customElements.define('pxl-product-specs', PxlProductSpecs);
})();